import {AlphaLabel} from '@github-ui/lifecycle-labels/alpha'
import {testIdProps} from '@github-ui/test-id-props'
import {StopIcon} from '@primer/octicons-react'
import {ActionList, NavList} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback, useMemo} from 'react'

import type {ClientMemexWorkflow} from '../../api/workflows/contracts'
import {useAutoAssignAgentEnabled} from '../../features/auto-assign-agent/use-auto-assign-agent-enabled'
import {
  isAutoAddWorkflow,
  isAutoAssignAgentWorkflow,
  isLastWorkflowOfType,
  isWorkflowPersisted,
  workflowTypeAsIdentity,
} from '../../helpers/workflow-utilities'
import {validateWorkflow} from '../../helpers/workflow-validation'
import {useProjectRouteParams} from '../../router/use-project-route-params'
import {PROJECT_WORKFLOW_CLIENT_ID_ROUTE} from '../../routes'
import {useWorkflows} from '../../state-providers/workflows/use-workflows'
import {WorkflowResources} from '../../strings'
import {NavLinkActionListItem} from '../react-router/action-list-nav-link-item'
import styles from './automation-side-nav.module.css'
import {WorkflowMenu} from './workflow-menu'

type AutomationSideNavProps = {
  activeWorkflow: ClientMemexWorkflow
}

export const AutomationSideNav = memo(({activeWorkflow}: AutomationSideNavProps) => {
  const {
    workflows,
    workflowWithTriggerTypeIsEnableable,
    resetWorkflowMenuStates,
    isWorkflowMenuOpen,
    getWorkflowMenuItemsMap,
  } = useWorkflows()

  const autoAssignAgentEnabled = useAutoAssignAgentEnabled()

  const onNavItemClickHandler = useCallback(() => resetWorkflowMenuStates(), [resetWorkflowMenuStates])

  const routeParams = useProjectRouteParams()

  const {agentWorkflows, defaultWorkflows} = useMemo(() => {
    const agent: Array<ClientMemexWorkflow> = []
    const defaults: Array<ClientMemexWorkflow> = []
    for (const workflow of workflows) {
      if (isAutoAssignAgentWorkflow(workflow)) {
        agent.push(workflow)
      } else {
        defaults.push(workflow)
      }
    }
    return {agentWorkflows: agent, defaultWorkflows: defaults}
  }, [workflows])

  const renderWorkflowItem = (workflow: ClientMemexWorkflow) => {
    const enableable = workflowWithTriggerTypeIsEnableable(workflow.triggerType)
    const isActive = workflow.clientId === activeWorkflow.clientId
    const isNonPersistedUserWorkflow = workflow.isUserWorkflow && !isWorkflowPersisted(workflow)
    const showDelete = !isLastWorkflowOfType(workflows, workflow)
    const hasMenu = isWorkflowPersisted(workflow) && (showDelete || isAutoAddWorkflow(workflow))

    return (
      <NavLinkActionListItem
        to={{
          pathname: PROJECT_WORKFLOW_CLIENT_ID_ROUTE.generatePath({
            ...routeParams,
            workflowClientId: workflow.clientId,
          }),
        }}
        onClick={onNavItemClickHandler}
        tabIndex={enableable ? 0 : -1}
        isActive={isActive}
        key={workflow.clientId}
        className={clsx(styles.workflowNavItem, enableable && styles.enableableWorkflowNavItem)}
        {...testIdProps(`workflow-nav-item-${workflowTypeAsIdentity(workflow)}`)}
      >
        {/* eslint-disable-next-line primer-react/direct-slot-children */}
        <NavList.LeadingVisual>
          <ProjectWorkflowIcon
            workflow={workflow}
            isActive={isActive}
            isNonPersistedUserWorkflow={isNonPersistedUserWorkflow}
          />
        </NavList.LeadingVisual>
        <WorkflowName name={workflow.name} isNonPersistedUserWorkflow={isNonPersistedUserWorkflow} />
        {hasMenu ? (
          /* eslint-disable-next-line primer-react/direct-slot-children */
          <NavList.TrailingVisual
            className={clsx(styles.workflowNavItemMenu, isWorkflowMenuOpen(workflow.clientId) && styles.menuOpen)}
          >
            <WorkflowMenu
              key={`workflow-nav-list-item-${workflow.clientId}`}
              ref={(node: HTMLButtonElement) => {
                const map = getWorkflowMenuItemsMap()
                if (node) {
                  map.set(workflow.clientId, node)
                } else {
                  map.delete(workflow.clientId)
                }
              }}
              workflow={workflow}
              showDelete={showDelete}
              isOpen={isWorkflowMenuOpen(workflow.clientId)}
            />
          </NavList.TrailingVisual>
        ) : null}
      </NavLinkActionListItem>
    )
  }

  return (
    <NavList className={styles.NavList} {...testIdProps('settings-side-nav')}>
      {autoAssignAgentEnabled && agentWorkflows.length > 0 && (
        <NavList.Group>
          <ActionList.GroupHeading as="h2">
            {WorkflowResources.agentWorkflowsGroupLabel}
            <AlphaLabel className={styles.previewBadge} />
          </ActionList.GroupHeading>
          {agentWorkflows.map(renderWorkflowItem)}
        </NavList.Group>
      )}
      <NavList.Group>
        {/* Override default h3 heading
            see: https://github.com/primer/react/blob/f1c18250b807e247d03b8ea8a6b4954b819d18e3/src/ActionList/Group.tsx#L58
        */}
        <ActionList.GroupHeading as="h2">{WorkflowResources.defaultWorkflowsGroupLabel}</ActionList.GroupHeading>
        {defaultWorkflows.map(renderWorkflowItem)}
      </NavList.Group>
    </NavList>
  )
})

AutomationSideNav.displayName = 'AutomationSideNav'

type ProjectWorkflowIconProps = {
  workflow: ClientMemexWorkflow
  isActive: boolean
  isNonPersistedUserWorkflow: boolean
}

const ProjectWorkflowIcon: React.FC<ProjectWorkflowIconProps> = ({workflow, isActive, isNonPersistedUserWorkflow}) => {
  if (isNonPersistedUserWorkflow) {
    return (
      <span className={styles.workflowIconContainer}>
        <span className={clsx(styles.workflowDot, styles.userWorkflowIcon)} />
      </span>
    )
  }
  if (workflow.enabled) {
    return (
      <span className={styles.workflowIconContainer}>
        <span className={clsx(styles.workflowDot, styles.enabledWorkflowIcon)} />
      </span>
    )
  } else if (validateWorkflow(workflow).isValid) {
    return (
      <span className={styles.workflowIconContainer}>
        <span
          className={clsx(
            styles.workflowDot,
            styles.workflowDotOutline,
            styles.validDisabledWorkflowIcon,
            isActive && styles.activeWorkflowIcon,
          )}
        />
      </span>
    )
  } else {
    return <StopIcon size={16} className={styles.invalidWorkflowIcon} />
  }
}

type WorkflowNameProps = {
  name: string
  isNonPersistedUserWorkflow: boolean
}

const WorkflowName: React.FC<WorkflowNameProps> = ({name, isNonPersistedUserWorkflow}) => {
  if (isNonPersistedUserWorkflow) {
    return <span className={styles.userWorkflowName}>{name}</span>
  }
  return <span>{name}</span>
}
